import {
    Box,
    Button,
    Card,
    Container,
    FormControl,
    Autocomplete,
    Stack,
    TextField,
    Typography,
    CircularProgress
} from "@mui/material";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import {useEffect, useRef, useState} from "react";
import { Layout } from "../layout/Layout";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DemoContainer} from "@mui/x-date-pickers/internals/demo";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {getOrdersByDate, getOrdersByDateAndClient} from "../../api/orderService";
import {Order} from "../../types/order";
import {useHeaders} from "../../hooks/useHeaders";
import {clientGetAll} from "../../api/clientService";
import {setClients} from "../../features/clients/clientSlice";
import {Client} from "../../types/client";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../app/store";
import {resetOrders, setOrders} from "../../features/orders/orderSlice";
import {useGlobalData} from "../../hooks/useGlobalData";
import {ReportsTable} from "./ReportsTable";
import * as XLSX from 'xlsx';
import {Auth} from "../../types/auth";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {OrderReceiptPdf2} from "../orders/orderReceiptPdf/OrderReceiptPdf2";
import {OrderInvoicesPdf} from "../orders/orderReceiptPdf/OrderInvoicesPdf";

interface TransformedOrder {
    "Numri i porosis": string;
    "Data e porosis": string;
    Klienti: string;
    "Numri i leternjoftimit": string;
    Lloji: string;
    "Sasia (kg)": number | string;
    Çmimi: string;
    Totali: string;
}

interface FilterData {
    client: Client;
    clients: Client[];
    orders: TransformedOrder[];
    startDate: dayjs.Dayjs;
    endDate: dayjs.Dayjs;
    isFilteredByDate: boolean;
    isFilteredByClient: boolean;
}

export const Reports = () => {
    const headers = useHeaders();
    const dispatch = useDispatch();
    const clients = useSelector((state: RootState) => state.clients);
    const orders = useSelector((state: RootState) => state.orders);
    const [filterData, setFilterData] = useState<FilterData>({
        client: {},
        clients: [],
        orders: [],
        startDate: dayjs(new Date()),
        endDate: dayjs(new Date()),
        isFilteredByDate: false,
        isFilteredByClient: false

    });
    const globalData = useGlobalData();
    const defaultProps = {
        options: filterData.clients,
        getOptionLabel: (option: Client) => `${option.firstName} ${option.lastName}`,
    };
    const [totalPrice, setTotalPrice] = useState<number | string>(0);
    const [totalKilograms, setTotalKilograms] = useState(0);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const tableRef = useRef<HTMLDivElement>(null);
    const invoicesRef = useRef<HTMLDivElement>(null);
    const [isGeneratingInvoicesPDF, setIsGeneratingInvoicesPDF] = useState(false);

    useEffect(() => {
        const getClients = async () => {
            dispatch(resetOrders());
            const clientsResponse: any = await clientGetAll(headers);

            if(clientsResponse)
                dispatch(setClients(clientsResponse as Client[]));
        }

        getClients();
    }, [dispatch]);

    const onDateChange = (key: any, val: any) => {
        setFilterData({
            ...filterData,
            [key] : val
        });
        console.log(clients)
    };

    const filterByDate = async () => {
        try{
            const data = {
                startDate: filterData.startDate?.format('YYYY-MM-DD'),
                endDate: filterData.endDate?.format('YYYY-MM-DD')
            }

            let transformedOrders: TransformedOrder[] = [];
            const orders = await getOrdersByDate(data, headers);

            if(orders){
                dispatch(setOrders(orders as Order[]));
                transformedOrders = orders.map((order: any) => ({
                    "Numri i porosis": String(order.orderNumber ?? ''),
                    "Data e porosis": dayjs(order.orderDate).format('DD.MM.YYYY'),
                    Klienti: `${order.idClient.firstName} ${order.idClient.lastName}`,
                    "Numri i leternjoftimit": order.idClient.nationalId,
                    Lloji: order.type,
                    "Sasia (kg)": order.quantity_per_kg,
                    Çmimi: order.price,
                    Totali: order.totalPrice
                }));

                const totalKg = transformedOrders.reduce((acc, order) => acc + Number(order["Sasia (kg)"]), 0);
                const totalPrc  = transformedOrders.reduce((acc, order) => acc + parseFloat(order.Totali), 0);

                setTotalKilograms(totalKg);
                setTotalPrice(totalPrc.toFixed(2));

                transformedOrders.push({
                    "Numri i porosis": '',
                    "Data e porosis": '',
                    Klienti: '',
                    "Numri i leternjoftimit": '',
                    Lloji: '',
                    "Sasia (kg)": 'Kg: ' + totalKg,
                    Çmimi: '',
                    Totali: 'Totali: ' + totalPrc.toFixed(2)
                });

                setFilterData({...filterData, })
            }

            const clientIds = new Set(orders.map((order) => order?.idClient?.idClient));
            const filteredClients = clients.filter(client => clientIds.has(client.idClient));

            setFilterData({
                ...filterData,
                clients: filteredClients,
                isFilteredByDate: true,
                orders: transformedOrders
            });
        }catch (error){
            console.error('Failed getting orders', error);
        }
    }

    const filterByDateAndClient = async (value: any) => {
        try{
            console.log(value)
            const newIdClient = value?.idClient ?? '';
            setFilterData(prev => ({
                ...prev,
                idClient: newIdClient
            }));
            const data = {
                idClient: Number(value?.idClient ?? ''),
                startDate: filterData.startDate?.format('YYYY-MM-DD'),
                endDate: filterData.endDate?.format('YYYY-MM-DD')
            }

            const orders = await getOrdersByDateAndClient(data, headers);

            if(orders){
                dispatch(setOrders(orders as Order[]));
                setFilterData({...filterData, isFilteredByClient: true});
                const totalKg = orders.reduce((acc, order) => acc + Number(order.quantity_per_kg), 0);
                const totalPrc = orders.reduce((acc, order) => acc + Number(order.totalPrice ?? 0), 0);

                setTotalKilograms(totalKg);
                setTotalPrice(totalPrc.toFixed(2));
            }
        }catch (error){
            console.error('Failed getting orders', error);
        }
    }

    const handleOnExport = () => {
        let wb = XLSX.utils.book_new();
        let ws = XLSX.utils.json_to_sheet(filterData.orders);

        XLSX.utils.book_append_sheet(wb, ws, `Raports`);
        XLSX.writeFile(wb, `Raports(${dayjs(filterData.startDate).format('DD.MM.YYYY')}-${dayjs(filterData.endDate).format('DD.MM.YYYY')}).xlsx`);
    }

    const downloadPDF = async () => {
        if (!tableRef.current) return;

        setIsGeneratingPDF(true);
        
        setTimeout(async () => {
            try {
                const element = tableRef.current;
                if (!element) {
                    setIsGeneratingPDF(false);
                    return;
                }

                // Temporarily make visible for capture
                const originalVisibility = element.style.visibility;
                const originalOpacity = element.style.opacity;
                element.style.visibility = 'visible';
                element.style.opacity = '1';
                
                await new Promise(resolve => setTimeout(resolve, 100));

                const canvas = await html2canvas(element, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff'
                });

                const imgData = canvas.toDataURL('image/jpeg', 0.8);
                const pdf = new jsPDF('p', 'mm', 'a4');
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();
                const imgWidth = canvas.width;
                const imgHeight = canvas.height;
                const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
                const imgX = (pdfWidth - imgWidth * ratio) / 2;
                const imgY = 5;

                pdf.addImage(imgData, 'JPEG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
                
                // Restore original visibility
                element.style.visibility = originalVisibility;
                element.style.opacity = originalOpacity;
                
                pdf.save(`Raports(${dayjs(filterData.startDate).format('DD.MM.YYYY')}-${dayjs(filterData.endDate).format('DD.MM.YYYY')}).pdf`);
                setIsGeneratingPDF(false);
            } catch (error) {
                console.error('Error generating PDF:', error);
                setIsGeneratingPDF(false);
            }
        }, 300);
    };

    const downloadInvoicesPDF = async () => {
        if (!invoicesRef.current || orders.length === 0) return;

        setIsGeneratingInvoicesPDF(true);
        
        setTimeout(async () => {
            try {
                const element = invoicesRef.current;
                if (!element) {
                    setIsGeneratingInvoicesPDF(false);
                    return;
                }

                // Temporarily make visible for capture
                const originalVisibility = element.style.visibility;
                const originalOpacity = element.style.opacity;
                element.style.visibility = 'visible';
                element.style.opacity = '1';
                
                await new Promise(resolve => setTimeout(resolve, 100));

                const pdf = new jsPDF('p', 'mm', 'a4');

                // Get all invoice boxes (direct children of the ref)
                const invoiceBoxes = element.children;
                
                for (let i = 0; i < invoiceBoxes.length; i++) {
                    const invoiceBox = invoiceBoxes[i] as HTMLElement;
                    
                    if (i > 0) {
                        pdf.addPage();
                    }
                    
                    // Capture each invoice individually
                    const canvas = await html2canvas(invoiceBox, {
                        scale: 2.5,
                        useCORS: true,
                        logging: false,
                        backgroundColor: '#ffffff',
                        allowTaint: true
                    });

                    const imgData = canvas.toDataURL('image/jpeg', 0.85);
                    
                    // Add image to fit A4 page
                    pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297);
                }
                
                // Restore original visibility
                element.style.visibility = originalVisibility;
                element.style.opacity = originalOpacity;
                
                pdf.save(`Fatura(${dayjs(filterData.startDate).format('DD.MM.YYYY')}-${dayjs(filterData.endDate).format('DD.MM.YYYY')}).pdf`);
                setIsGeneratingInvoicesPDF(false);
            } catch (error) {
                console.error('Error generating PDF:', error);
                setIsGeneratingInvoicesPDF(false);
            }
        }, 300);
    };

    return(
        <Layout>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    py: 8
                }}
            >
                <Container maxWidth="xl">
                    <Stack spacing={3}>
                        <Card sx={{
                            py: 4,
                            '@media screen and (max-width: 576px)': { px: 3}
                        }}>
                            <Stack
                                direction="row"
                                flexWrap="wrap"
                                gap={2}
                                justifyContent="center"
                                sx={{'@media screen and (max-width: 576px)': { flexDirection: 'column' }}}
                            >
                                <FormControl sx={{width: '37%', '@media screen and (max-width: 576px)': { width: '100%' }}}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DemoContainer components={['DatePicker']} sx={{paddingTop: '0 !important'}}>
                                            <DatePicker
                                                label="Nga"
                                                value={dayjs(filterData?.startDate)}
                                                onChange={(val) => onDateChange('startDate', val)}
                                                format={'DD/MM/YYYY'}
                                                sx={{width: '100% !important'}}
                                            />
                                        </DemoContainer>
                                    </LocalizationProvider>
                                </FormControl>

                                <FormControl sx={{width: '37%', '@media screen and (max-width: 576px)': { width: '100%' }}}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DemoContainer components={['DatePicker']} sx={{paddingTop: '0 !important'}}>
                                            <DatePicker
                                                label="Deri"
                                                value={dayjs(filterData?.endDate)}
                                                onChange={(val) => onDateChange('endDate', val)}
                                                format={'DD/MM/YYYY'}
                                                sx={{width: '100% !important'}}
                                            />
                                        </DemoContainer>
                                    </LocalizationProvider>
                                </FormControl>

                                <Button
                                    variant="contained"
                                    onClick={filterByDate}
                                    sx={{width: '15%', '@media screen and (max-width: 576px)': {width: '100%'}}}
                                >
                                    Gjenero Raportin
                                </Button>
                            </Stack>
                        </Card>

                        {orders.length > 0 &&
                            <Stack spacing={3}>
                                <Card sx={{
                                    py: 4,
                                    px: 5,
                                    '@media screen and (max-width: 576px)': {px: 3}
                                }}>
                                    <Box display="flex" justifyContent="end" mb="20px">
                                        <Autocomplete
                                            {...defaultProps}
                                            id="clear-on-escape"
                                            onChange={(event, value) => filterByDateAndClient(value)}
                                            value={clients.find(client => client.idClient === filterData.client.idClient) || null}
                                            clearOnEscape
                                            renderInput={(params) => (
                                                <TextField {...params} label="Klienti" variant="standard"
                                                           sx={{'& .MuiInputLabel-root': {lineHeight: '11px'}}}
                                                />
                                            )}
                                            sx={{
                                                width: '25%',
                                                mr: '15px',
                                                '@media screen and (max-width: 576px)': {width: '60%'}
                                            }}
                                            // isOptionEqualToValue={(option, value) => option.idClient === value.idClient}
                                        />
                                        {/*<Link to={`/reports/pdf-viewer`}>*/}
                                        <Button onClick={handleOnExport} sx={{
                                            backgroundColor: '#F3F4F6',
                                            color: 'black',
                                            mr: 1
                                        }}><DownloadIcon /></Button>
                                        <Button 
                                            onClick={downloadPDF} 
                                            sx={{
                                                backgroundColor: '#F3F4F6',
                                                color: 'black',
                                                mr: 1,
                                                minWidth: '40px'
                                            }}
                                            disabled={isGeneratingPDF}
                                        >
                                            {isGeneratingPDF ? <CircularProgress size={20} sx={{ color: 'black' }} /> : <PictureAsPdfIcon />}
                                        </Button>
                                        <Button 
                                            onClick={downloadInvoicesPDF} 
                                            variant="contained"
                                            sx={{
                                                backgroundColor: '#1976d2',
                                                color: 'white',
                                                textTransform: 'none',
                                                fontSize: '14px',
                                                px: 2,
                                                minWidth: '180px',
                                                '&:hover': {
                                                    backgroundColor: '#1565c0'
                                                }
                                            }} 
                                            disabled={orders.length === 0 || isGeneratingInvoicesPDF}
                                            startIcon={isGeneratingInvoicesPDF ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                                        >
                                            {isGeneratingInvoicesPDF ? 'Duke shkarkuar...' : 'Shkarko Faturat PDF'}
                                        </Button>
                                        {/*</Link>*/}
                                    </Box>
                                    <ReportsTable orders={orders}/>
                                </Card>
                            </Stack>
                        }

                        {
                            (orders.length === 0 && filterData.isFilteredByDate) &&
                            (
                                <Stack spacing={3}>
                                    <Card sx={{
                                        py: 4,
                                        px: 5,
                                        '@media screen and (max-width: 576px)': {px: 3}
                                    }}>
                                        <Typography>Nuk është gjetur asnjë blerje</Typography>
                                    </Card>
                                </Stack>
                            )
                        }
                    </Stack>
                </Container>
                    <OrderReceiptPdf2
                        ref={tableRef}
                        orders={orders}
                        global={globalData}
                        totalPrice={totalPrice}
                        totalKg={totalKilograms}
                        isGeneratingPDF={isGeneratingPDF}
                    />
                    <OrderInvoicesPdf
                        ref={invoicesRef}
                        orders={orders}
                        global={globalData}
                        isGeneratingPDF={isGeneratingInvoicesPDF}
                    />
            </Box>
        </Layout>
    )
}

